import React, { useState, useEffect } from "react";
import { Phone, Menu, X, Facebook, Instagram } from "lucide-react";
import { SITE } from "../lib/site";

const NAV = [
  { href: "#services", label: "Services" },
  { href: "#default-service", label: "Our Standard" },
  { href: "#gallery", label: "Our Work" },
  { href: "#areas", label: "Areas" },
  { href: "#faq", label: "FAQ" },
  { href: "#quote", label: "Get Quote" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "vgc-glass shadow-[0_4px_24px_rgba(15,76,129,0.06)]" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between px-5 lg:px-8 h-[72px]">
        <a href="#top" className="flex items-center gap-3" data-testid="brand-link">
          <span className="h-12 w-12 flex items-center justify-center">
            <img
              src={SITE.logoUrl}
              alt="Vision Glass Care"
              className="h-12 w-12 object-contain"
            />
          </span>
          <span className="hidden sm:flex flex-col leading-tight">
            <span className="font-[Outfit] font-bold text-[15px] text-[#0F4C81] tracking-tight">
              Vision Glass Care
            </span>
            <span className="text-[11px] uppercase tracking-[0.18em] text-[#64748B]">
              Baton Rouge · LA
            </span>
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-8" data-testid="primary-nav">
          {NAV.map((item) => (
            <a
              key={item.href}
              href={item.href}
              className="text-sm font-medium text-slate-700 hover:text-[#0F4C81] transition-colors"
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {item.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <a
            href={SITE.facebookUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Facebook"
            data-testid="header-facebook"
            className="hidden md:inline-flex h-10 w-10 items-center justify-center rounded-full vgc-glass text-[#0F4C81] hover:text-[#00B4D8] transition-colors"
          >
            <Facebook className="h-4 w-4" />
          </a>
          <a
            href={SITE.instagramUrl}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Instagram"
            data-testid="header-instagram"
            className="hidden md:inline-flex h-10 w-10 items-center justify-center rounded-full vgc-glass text-[#0F4C81] hover:text-[#00B4D8] transition-colors"
          >
            <Instagram className="h-4 w-4" />
          </a>
          <a
            href={`tel:${SITE.phoneTel}`}
            className="hidden sm:inline-flex items-center gap-2 px-4 h-11 rounded-full bg-[#0F4C81] text-white text-sm font-semibold hover:bg-[#0B3A66] transition-colors focus-brand"
            data-testid="header-call-btn"
          >
            <Phone className="h-4 w-4" />
            {SITE.phoneDisplay}
          </a>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden inline-flex items-center justify-center h-11 w-11 rounded-full vgc-glass focus-brand"
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-blue-100 vgc-glass" data-testid="mobile-nav">
          <nav className="flex flex-col px-5 py-3">
            {NAV.map((item) => (
              <a
                key={item.href}
                href={item.href}
                onClick={() => setOpen(false)}
                className="py-3 text-base font-medium text-slate-800 border-b border-blue-50 last:border-0"
                data-testid={`mnav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {item.label}
              </a>
            ))}
            <a
              href={`tel:${SITE.phoneTel}`}
              className="mt-3 mb-2 inline-flex items-center justify-center gap-2 h-12 rounded-full bg-[#0F4C81] text-white font-semibold"
              data-testid="mobile-call-btn"
            >
              <Phone className="h-4 w-4" />
              Call {SITE.phoneDisplay}
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}
